import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, ShieldCheckIcon, ActivityIcon, NetworkIcon, LayersIcon, LockIcon, CheckCircle2Icon, TerminalIcon, UserCheckIcon, FileTextIcon, BarChartIcon, SmartphoneIcon, GlobeIcon, MonitorIcon, CreditCardIcon, MessageSquareIcon, InboxIcon, WalletIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  traditional: 'Multiple disconnected tools',
  traditionalFr: 'Multiples outils déconnectés',
  bciti: 'Verified identity',
  bcitiFr: 'Identité vérifiée'
}, {
  traditional: 'No identity context',
  traditionalFr: "Aucun contexte d'identité",
  bciti: 'Organizational context',
  bcitiFr: 'Contexte organisationnel'
}, {
  traditional: 'Manual lookups',
  traditionalFr: 'Recherches manuelles',
  bciti: 'Unified data',
  bcitiFr: 'Données unifiées'
}, {
  traditional: 'Fragmented information',
  traditionalFr: 'Information fragmentée',
  bciti: 'Verified answers',
  bcitiFr: 'Réponses vérifiées'
}, {
  traditional: 'Reactive operations',
  traditionalFr: 'Opérations réactives',
  bciti: 'Guided actions',
  bcitiFr: 'Actions guidées'
}];
const MODULES = [{
  name: 'Identity',
  nameFr: 'Identité'
}, {
  name: 'Digital Card',
  nameFr: 'Carte numérique'
}, {
  name: 'Communications',
  nameFr: 'Communications'
}, {
  name: 'Requests',
  nameFr: 'Requêtes'
}, {
  name: 'Collect',
  nameFr: 'Collecte'
}, {
  name: 'Analytics',
  nameFr: 'Analytique'
}];
const WORKFLOW = [{
  en: 'Verified Identity',
  fr: 'Identité vérifiée'
}, {
  en: 'Platform Context',
  fr: 'Contexte de la plateforme'
}, {
  en: 'Question',
  fr: 'Question'
}, {
  en: 'Verified Answer',
  fr: 'Réponse vérifiée'
}, {
  en: 'Authorized Action',
  fr: 'Action autorisée'
}];
const SURFACES = [{
  en: 'Citizen Mobile App',
  fr: 'Application mobile citoyenne'
}, {
  en: 'Employee Operations Dashboard',
  fr: 'Tableau de bord des opérations pour les employés'
}, {
  en: 'Executive Analytics Terminal',
  fr: "Terminal d'analytique pour la direction"
}, {
  en: 'Automated Service Workflows',
  fr: 'Flux de travail de service automatisés'
}];
const PROOF_CARDS = [{
  label: 'Deployment',
  labelFr: 'Déploiement',
  title: 'Clarence-Rockland',
  titleFr: 'Clarence-Rockland',
  desc: 'ATNA running in a live municipal environment.',
  descFr: 'ATNA en fonction dans un environnement municipal réel.'
}, {
  label: 'Integration',
  labelFr: 'Intégration',
  title: 'Collect + ATNA',
  titleFr: 'Collecte + ATNA',
  desc: 'Operational intelligence wired into a production module.',
  descFr: 'Intelligence opérationnelle intégrée à un module en production.'
}, {
  label: 'Status',
  labelFr: 'Statut',
  title: 'Live Deployment',
  titleFr: 'Déploiement actif',
  desc: 'Verifiable, in production, available today.',
  descFr: 'Vérifiable, en production, disponible aujourd\u2019hui.'
}];
const PROOF_CHANNELS = [{
  en: 'Mobile',
  fr: 'Mobile'
}, {
  en: 'Portal',
  fr: 'Portail'
}, {
  en: 'Web',
  fr: 'Web'
}, {
  en: 'Modules',
  fr: 'Modules'
}];
const PROOF_PRINCIPLES = [{
  en: 'Trust',
  fr: 'Confiance'
}, {
  en: 'Verify',
  fr: 'Vérifier'
}, {
  en: 'Act',
  fr: 'Agir'
}];
const TRUST_CARDS = [{
  title: 'Availability',
  titleFr: 'Disponibilité',
  items: ['Mobile App', 'Portal', 'Website', 'Embedded in every module'],
  itemsFr: ['Application mobile', 'Portail', 'Site web', 'Intégré à chaque module']
}, {
  title: 'Guided Experiences',
  titleFr: 'Expériences guidées',
  items: ['Buttons first', 'Fast navigation', 'Structured journeys'],
  itemsFr: ['Les boutons d\u2019abord', 'Navigation rapide', 'Parcours structurés']
}, {
  title: 'Grounded Answers',
  titleFr: 'Réponses fondées',
  items: ['Approved content', 'Organizational data', 'Verified responses'],
  itemsFr: ['Contenu approuvé', 'Données organisationnelles', 'Réponses vérifiées']
}, {
  title: 'Guardrails',
  titleFr: 'Garde-fous',
  items: ['Law 25', 'Canadian Hosting', 'Auditability', 'Traceability'],
  itemsFr: ['Loi 25', 'Hébergement canadien', 'Traçabilité', 'Traçabilité des sources']
}];
const TOUCHPOINTS = [{
  name: 'Mobile App',
  nameFr: 'Application mobile',
  icon: SmartphoneIcon
}, {
  name: 'Portal',
  nameFr: 'Portail',
  icon: MonitorIcon
}, {
  name: 'Website',
  nameFr: 'Site web',
  icon: GlobeIcon
}, {
  name: 'Digital Card',
  nameFr: 'Carte numérique',
  icon: CreditCardIcon
}, {
  name: 'Communications',
  nameFr: 'Communications',
  icon: MessageSquareIcon
}, {
  name: 'Requests',
  nameFr: 'Requêtes',
  icon: InboxIcon
}, {
  name: 'Collect',
  nameFr: 'Collecte',
  icon: WalletIcon
}, {
  name: 'Analytics',
  nameFr: 'Analytique',
  icon: BarChartIcon
}];
export function Atna() {
  const {
    lang
  } = useLanguage();
  useEffect(() => {
    track('Page Viewed', {
      page: 'ATNA Assistant'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1018">
      <Header data-id="element-1019" />

      <main className="pt-[120px]" data-id="element-1020">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-32 px-6" data-id="element-1021">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center" data-id="element-1022">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="lg:col-span-5" data-id="element-1023">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-6" data-id="element-1024">
                {lang === 'FR' ? 'Faire confiance. Vérifier. Agir.' : 'Trust. Verify. Act.'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-1025">
                {lang === 'FR' ? <>
                    La bonne réponse.
                    <br data-id="element-1026" />
                    Pour la bonne personne.
                    <br data-id="element-1027" />
                    Avec l'action.
                  </> : <>
                    The right answer.
                    <br data-id="element-1028" />
                    For the right person.
                    <br data-id="element-1029" />
                    With action.
                  </>}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-4" data-id="element-1030">
                {lang === 'FR' ? "ATNA comprend l'identité vérifiée, le contexte organisationnel et les données opérationnelles." : 'ATNA understands verified identity, organizational context and operational data.'}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-1031">
                {lang === 'FR' ? 'Disponible sur mobile, portail, web et chaque module BCITI.' : 'Available across mobile, portal, web and every BCITI module.'}
              </p>
              <div className="flex flex-wrap items-center gap-4 mb-10" data-id="element-1032">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'ATNA Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1033">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1034" />
                </Link>
                <button onClick={() => {
                window.dispatchEvent(new CustomEvent('atna:open'));
                track('Ask ATNA Clicked', {
                  location: 'ATNA Hero'
                });
              }} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-1035">
                  {lang === 'FR' ? 'Demander à ATNA' : 'Ask ATNA'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1036" />
                </button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-6" data-id="element-1037">
                <div className="flex items-center gap-2" data-id="element-1038">
                  <ShieldCheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1039" />
                  <span className="font-sans text-xs font-medium text-[#121212]/60 uppercase tracking-wider" data-id="element-1040">
                    {lang === 'FR' ? 'Identité vérifiée' : 'Verified Identity'}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1041">
                  <ShieldCheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1042" />
                  <span className="font-sans text-xs font-medium text-[#121212]/60 uppercase tracking-wider" data-id="element-1043">
                    {lang === 'FR' ? 'Données vérifiées' : 'Verified Data'}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1044">
                  <ShieldCheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1045" />
                  <span className="font-sans text-xs font-medium text-[#121212]/60 uppercase tracking-wider" data-id="element-1046">
                    {lang === 'FR' ? 'Action autorisée' : 'Authorized Action'}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* HERO VISUAL: Executive Operational Intelligence Interface */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="lg:col-span-7 flex justify-center lg:justify-end" data-id="element-1047">
              <div className="w-full max-w-2xl border border-[#E4E6EC] bg-[#FAFAFB] p-4" data-id="element-1048">
                <div className="border border-[#E4E6EC] bg-white flex flex-col" data-id="element-1049">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-1050">
                    <div className="flex items-center gap-2" data-id="element-1051">
                      <TerminalIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1052" />
                      <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-1053">
                        {lang === 'FR' ? "Interface d'intelligence opérationnelle pour la direction" : 'Executive Operational Intelligence Interface'}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1.5 font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#4F46E5] border border-[#4F46E5]/30 px-2 py-1" data-id="element-1054">
                      <span className="w-1.5 h-1.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-1055" />
                      {lang === 'FR' ? 'Actif' : 'Active'}
                    </span>
                  </div>

                  {/* Main Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 border-b border-[#E4E6EC]" data-id="element-1056">
                    {/* Left Panel */}
                    <div className="p-5 border-b sm:border-b-0 sm:border-r border-[#E4E6EC] bg-[#FAFAFB]/50" data-id="element-1057">
                      <div className="font-sans text-[10px] font-semibold tracking-widest uppercase text-[#121212]/40 mb-4" data-id="element-1058">
                        {lang === 'FR' ? 'Contexte' : 'Context'}
                      </div>
                      <div className="space-y-4" data-id="element-1059">
                        <div data-id="element-1060">
                          <div className="font-sans text-[11px] text-[#121212]/50 mb-1" data-id="element-1061">
                            {lang === 'FR' ? 'Identité vérifiée' : 'Verified Identity'}
                          </div>
                          <div className="font-sans text-sm font-medium text-[#121212] flex items-center gap-2" data-id="element-1062">
                            <UserCheckIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-1063" />
                            M. Tremblay
                          </div>
                        </div>
                        <div data-id="element-1064">
                          <div className="font-sans text-[11px] text-[#121212]/50 mb-1" data-id="element-1065">
                            {lang === 'FR' ? 'Localisation' : 'Location'}
                          </div>
                          <div className="font-sans text-sm font-medium text-[#121212]" data-id="element-1066">
                            Zone C
                          </div>
                        </div>
                        <div data-id="element-1067">
                          <div className="font-sans text-[11px] text-[#121212]/50 mb-1" data-id="element-1068">
                            {lang === 'FR' ? 'Rôle' : 'Role'}
                          </div>
                          <div className="font-sans text-sm font-medium text-[#121212]" data-id="element-1069">
                            {lang === 'FR' ? 'Résident' : 'Resident'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Center Panel */}
                    <div className="p-5 border-b sm:border-b-0 sm:border-r border-[#E4E6EC]" data-id="element-1070">
                      <div className="font-sans text-[10px] font-semibold tracking-widest uppercase text-[#121212]/40 mb-4" data-id="element-1071">
                        {lang === 'FR' ? 'Question' : 'Question'}
                      </div>
                      <div className="font-serif text-lg text-[#121212] leading-relaxed" data-id="element-1072">
                        {lang === 'FR' ? "« Pourquoi ma facture d'eau est-elle plus élevée que d'habitude ce mois-ci ? »" : '"Why is my water bill higher than usual this month?"'}
                      </div>
                    </div>

                    {/* Right Panel */}
                    <div className="p-5" data-id="element-1073">
                      <div className="font-sans text-[10px] font-semibold tracking-widest uppercase text-[#4F46E5] mb-4" data-id="element-1074">
                        {lang === 'FR' ? 'Réponse vérifiée' : 'Verified Answer'}
                      </div>
                      <div className="font-sans text-sm text-[#121212] leading-relaxed mb-5" data-id="element-1075">
                        {lang === 'FR' ? 'Les données du compteur intelligent montrent un débit continu depuis 48 heures. Fuite probable détectée.' : 'Smart meter data shows continuous flow for 48 hours. Probable leak detected.'}
                      </div>
                      <div className="font-sans text-[10px] font-semibold tracking-widest uppercase text-[#4F46E5] mb-3" data-id="element-1076">
                        {lang === 'FR' ? 'Action recommandée' : 'Recommended Action'}
                      </div>
                      <div className="inline-flex items-center gap-2 bg-[#4F46E5]/5 border border-[#4F46E5]/20 px-3 py-2" data-id="element-1077">
                        <ShieldCheckIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1078" />
                        <span className="font-sans text-xs font-medium text-[#4F46E5]" data-id="element-1079">
                          {lang === 'FR' ? 'Dépêcher un technicien' : 'Dispatch Technician'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Panel */}
                  <div className="px-5 py-4 bg-[#FAFAFB]/30 flex flex-wrap gap-6 items-center justify-between" data-id="element-1080">
                    <div className="flex flex-wrap items-center gap-6" data-id="element-1081">
                      <div className="flex items-center gap-2" data-id="element-1082">
                        <div className="w-1.5 h-1.5 bg-[#4F46E5]" data-id="element-1083" />
                        <span className="font-sans text-[10px] uppercase tracking-wider text-[#121212]/50" data-id="element-1084">
                          {lang === 'FR' ? "Piste d'audit : active" : 'Audit Trail: Active'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1085">
                        <div className="w-1.5 h-1.5 bg-[#4F46E5]" data-id="element-1086" />
                        <span className="font-sans text-[10px] uppercase tracking-wider text-[#121212]/50" data-id="element-1087">
                          {lang === 'FR' ? 'Données sources : compteurs intelligents' : 'Source Data: Smart Water'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 font-sans text-[10px] uppercase tracking-wider text-[#4F46E5] font-medium" data-id="element-1088">
                      <LockIcon className="w-3 h-3" data-id="element-1089" />
                      {lang === 'FR' ? "Statut d'autorisation : vérifié" : 'Authorization Status: Verified'}
                    </div>
                  </div>
                </div>
                <div className="border-t border-[#E4E6EC] mt-4 pt-4" data-id="element-1090">
                  <p className="font-sans text-[11px] text-[#121212]/40 text-center" data-id="element-1091">
                    {lang === 'FR' ? 'Vue illustrative du produit' : 'Illustrative product view'}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CONTEXT CHANGES EVERYTHING */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1092">
          <div className="max-w-4xl mx-auto" data-id="element-1093">
            <div className="text-center mb-20" data-id="element-1094">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212]" data-id="element-1095">
                {lang === 'FR' ? 'Le contexte change tout.' : 'Context changes everything.'}
              </motion.h2>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-16" data-id="element-1096">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1097">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-1098">
                  {lang === 'FR' ? 'Systèmes traditionnels' : 'Traditional Systems'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-1099">
                  {lang === 'FR' ? 'Intelligence opérationnelle ATNA' : 'ATNA Operational Intelligence'}
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-1100">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-1101">
                    {lang === 'FR' ? row.traditionalFr : row.traditional}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-1102">
                    {lang === 'FR' ? row.bcitiFr : row.bciti}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-1103">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-1104" />
              <p className="font-serif text-3xl text-[#121212]/50" data-id="element-1105">
                {lang === 'FR' ? "L'intelligence sans contexte, c'est deviner." : 'Intelligence without context is guessing.'}
              </p>
              <p className="font-serif text-3xl text-[#121212] mt-2" data-id="element-1106">
                {lang === 'FR' ? 'Le contexte crée la confiance.' : 'Context creates confidence.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: EVERY MODULE GIVES ATNA CONTEXT */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1107">
          <div className="max-w-4xl mx-auto" data-id="element-1108">
            <div className="text-center mb-16" data-id="element-1109">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10" data-id="element-1110">
                {lang === 'FR' ? 'Chaque module donne du contexte à ATNA.' : 'Every module gives ATNA context.'}
              </motion.h2>

              {/* Modules */}
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-1111">
                {MODULES.map(mod => <span key={mod.name} className="inline-flex items-center gap-2 border border-[#E4E6EC] bg-white px-4 py-2 font-sans text-sm text-[#121212]/70" data-id="element-1112">
                    <span className="inline-block w-1.5 h-1.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-1113" />
                    {lang === 'FR' ? mod.nameFr : mod.name}
                  </span>)}
              </motion.div>

              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.15
            }} className="font-serif text-2xl text-[#121212]" data-id="element-1114">
                <p className="text-[#121212]/50" data-id="element-1115">
                  {lang === 'FR' ? 'Chaque module ajoute du contexte.' : 'Each module adds context.'}
                </p>
                <p className="mt-1" data-id="element-1116">
                  {lang === 'FR' ? 'Chaque contexte améliore la précision.' : 'Each context improves accuracy.'}
                </p>
              </motion.div>
            </div>

            {/* ONE workflow */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col items-center mb-16" data-id="element-1117">
              {WORKFLOW.map((node, i, arr) => {
              const isFirst = i === 0;
              const isLast = i === arr.length - 1;
              const highlight = isFirst || isLast;
              return <Fragment key={node.en} data-id="element-1118">
                    <div className={`w-full max-w-md py-4 px-6 text-center border font-sans text-sm ${highlight ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5] font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'}`} data-id="element-1119">
                      {lang === 'FR' ? node.fr : node.en}
                    </div>
                    {i < arr.length - 1 && <ArrowDownIcon className="w-4 h-4 text-[#E4E6EC] my-2" data-id="element-1120" />}
                  </Fragment>;
            })}
            </motion.div>

            {/* Statement */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-1121">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-1122" />
              <p className="font-serif text-3xl text-[#121212]/50" data-id="element-1123">
                {lang === 'FR' ? "Sans contexte, un assistant n'est qu'une fonctionnalité." : 'Without context, an assistant is a feature.'}
              </p>
              <p className="font-serif text-3xl text-[#121212] mt-2" data-id="element-1124">
                {lang === 'FR' ? 'Avec le contexte, il devient une infrastructure.' : 'With context, it becomes infrastructure.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3.5: ONE INTELLIGENCE LAYER — EVERY TOUCHPOINT */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1125">
          <div className="max-w-5xl mx-auto" data-id="element-1126">
            <div className="text-center mb-16" data-id="element-1127">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] leading-tight" data-id="element-1128">
                {lang === 'FR' ? <>
                    Une seule couche d'intelligence.
                    <br data-id="element-1129" />
                    Chaque point de contact.
                  </> : <>
                    One intelligence layer.
                    <br data-id="element-1130" />
                    Every touchpoint.
                  </>}
              </motion.h2>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-16" data-id="element-1131">
              {TOUCHPOINTS.map(point => {
              const Icon = point.icon;
              return <div key={point.name} className="bg-white p-8 flex flex-col items-start gap-4" data-id="element-1132">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.75} data-id="element-1133" />
                    <span className="font-serif text-xl text-[#121212]" data-id="element-1134">
                      {lang === 'FR' ? point.nameFr : point.name}
                    </span>
                  </div>;
            })}
            </motion.div>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.25
          }} className="text-center font-serif text-2xl text-[#121212] max-w-2xl mx-auto" data-id="element-1135">
              {lang === 'FR' ? 'La même couche d\u2019intelligence opère dans chaque expérience BCITI.' : 'The same intelligence layer operates across every BCITI experience.'}
            </motion.p>
          </div>
        </section>

        {/* SECTION 4: ATNA LIVES EVERYWHERE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1136">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1137">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-1138">
              {lang === 'FR' ? 'ATNA est partout.' : 'ATNA lives everywhere.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-16 max-w-2xl mx-auto" data-id="element-1139">
              {lang === 'FR' ? "Ce n'est pas une destination où l'on se rend. C'est une capacité intégrée à l'ensemble de votre surface opérationnelle." : 'It is not a destination you navigate to. It is a capability embedded across your entire operational surface area.'}
            </motion.p>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1140">
              {SURFACES.map(surface => <div key={surface.en} className="border border-[#E4E6EC] bg-white p-6 flex items-center gap-4 text-left" data-id="element-1141">
                  <LayersIcon className="w-5 h-5 text-[#4F46E5] shrink-0" data-id="element-1142" />
                  <span className="font-serif text-xl text-[#121212]" data-id="element-1143">
                    {lang === 'FR' ? surface.fr : surface.en}
                  </span>
                </div>)}
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: IN PRODUCTION — PROVEN PROOF */}
        <section id="production" className="py-32 px-6 border-t border-[#E4E6EC] scroll-mt-24" data-id="element-1144">
          <div className="max-w-5xl mx-auto" data-id="element-1145">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center leading-tight mb-16" data-id="element-1146">
              {lang === 'FR' ? <>
                  En production.
                  <br data-id="element-1147" />
                  Vérifiable.
                </> : <>
                  In production.
                  <br data-id="element-1148" />
                  Verifiable.
                </>}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-12" data-id="element-1149">
              {PROOF_CARDS.map((card, i) => <motion.div key={card.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white p-10" data-id="element-1150">
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase mb-4" data-id="element-1151">
                    {lang === 'FR' ? card.labelFr : card.label}
                  </div>
                  <div className="font-serif text-2xl md:text-3xl text-[#121212] mb-4" data-id="element-1152">
                    {lang === 'FR' ? card.titleFr : card.title}
                  </div>
                  <div className="font-sans text-sm text-[#121212]/55 leading-relaxed" data-id="element-1153">
                    {lang === 'FR' ? card.descFr : card.desc}
                  </div>
                </motion.div>)}
            </div>

            {/* Channels + principles */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-center justify-center gap-x-10 gap-y-6 mb-16" data-id="element-1154">
              <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-1155">
                {PROOF_CHANNELS.map(channel => <span key={channel.en} className="font-sans text-sm text-[#121212]/70 border border-[#E4E6EC] bg-white px-4 py-2" data-id="element-1156">
                    {lang === 'FR' ? channel.fr : channel.en}
                  </span>)}
              </div>
              <div className="hidden sm:block w-px h-6 bg-[#E4E6EC]" aria-hidden="true" data-id="element-1157" />
              <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-1158">
                {PROOF_PRINCIPLES.map(principle => <span key={principle.en} className="font-sans text-sm font-medium text-[#4F46E5] border border-[#4F46E5]/30 px-4 py-2" data-id="element-1159">
                    {lang === 'FR' ? principle.fr : principle.en}
                  </span>)}
              </div>
            </motion.div>

            {/* Supporting text + Ask ATNA */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-1160">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-1161" />
              <p className="font-serif text-2xl text-[#121212]" data-id="element-1162">
                {lang === 'FR' ? 'ATNA est en production à Clarence-Rockland.' : 'ATNA is live in Clarence-Rockland.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]/50 mb-8" data-id="element-1163">
                {lang === 'FR' ? 'Essayez Demander à ATNA par vous-même.' : 'Try Ask ATNA yourself.'}
              </p>
              <button onClick={() => {
              window.dispatchEvent(new CustomEvent('atna:open'));
              track('Ask ATNA Clicked', {
                location: 'ATNA Production Proof'
              });
            }} className="inline-flex items-center gap-2 px-8 py-4 border border-[#4F46E5] text-[#4F46E5] text-base font-medium rounded-none hover:bg-[#4F46E5] hover:text-white transition-colors" data-id="element-1164">
                {lang === 'FR' ? 'Demander à ATNA' : 'Ask ATNA'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-1165" />
              </button>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: BUILT TO BE TRUSTED */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1166">
          <div className="max-w-6xl mx-auto" data-id="element-1167">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-1168">
              {lang === 'FR' ? 'Conçu pour inspirer confiance.' : 'Built to be trusted.'}
            </motion.h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-1169">
              {TRUST_CARDS.map((card, i) => <motion.div key={card.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.08
            }} className="bg-white p-8" data-id="element-1170">
                  <div className="flex items-center gap-2 mb-6" data-id="element-1171">
                    <span className="inline-block w-2.5 h-2.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-1172" />
                    <h3 className="font-serif text-xl text-[#121212]" data-id="element-1173">
                      {lang === 'FR' ? card.titleFr : card.title}
                    </h3>
                  </div>
                  <ul className="space-y-3" data-id="element-1174">
                    {(lang === 'FR' ? card.itemsFr : card.items).map(item => <li key={item} className="flex items-start gap-3 font-sans text-sm text-[#121212]/70" data-id="element-1175">
                        <CheckCircle2Icon className="w-4 h-4 text-[#4F46E5] shrink-0 mt-0.5" data-id="element-1176" />
                        {item}
                      </li>)}
                  </ul>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 7: FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC]" data-id="element-1177">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1178">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-1179">
              {lang === 'FR' ? "Déployez la fondation. Activez l'intelligence." : 'Deploy the foundation. Enable the intelligence.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-12" data-id="element-1180">
              {lang === 'FR' ? "Commencez par l'identité. Étendez aux services. Laissez ATNA relier les points." : 'Start with Identity. Expand to Services. Let ATNA connect the dots.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-1181">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'ATNA Final CTA'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1182">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1183" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1184" />
    </div>;
}